import { useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

import { db } from "../../FirebaseConfig";
import { SavingOverlay } from "../../components/SavingOverlay";
import { confirmDestructive } from "../../lib/confirm";
import type { AeropuertoDoc, AvionDoc, DistanciaDoc } from "../../lib/types";
import { distanciaId } from "../../lib/types";

interface DistanciaRow extends DistanciaDoc {
  id: string;
}

interface AeropuertoRow extends AeropuertoDoc {
  id: string;
}

/**
 * /admin/distances — list / search / upsert / delete. Distancias doc id
 * is the canonical "LO|HI" pair so reading is direction-free. New
 * entries also write canonically.
 */
export function AdminDistancias() {
  const [rows, setRows] = useState<DistanciaRow[] | null>(null);
  const [aeros, setAeros] = useState<AeropuertoRow[]>([]);
  const [baseIcaos, setBaseIcaos] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<DistanciaRow | null>(null);

  useEffect(() => {
    const q1 = query(collection(db, "Distancias"), orderBy("lo"));
    const u1 = onSnapshot(q1, (snap) => {
      const list: DistanciaRow[] = [];
      snap.forEach((d) =>
        list.push({ id: d.id, ...(d.data() as DistanciaDoc) })
      );
      setRows(list);
    });
    const q2 = query(
      collection(db, "Aeropuertos"),
      orderBy("displayCombined")
    );
    const u2 = onSnapshot(q2, (snap) => {
      const list: AeropuertoRow[] = [];
      snap.forEach((d) =>
        list.push({ id: d.id, ...(d.data() as AeropuertoDoc) })
      );
      setAeros(list);
    });
    // Aviones home bases — Distancias display flips so the base is
    // always the "origin" column when present (otherwise canonical
    // LO|HI ordering).
    const u3 = onSnapshot(collection(db, "Aviones"), (snap) => {
      const set = new Set<string>();
      snap.forEach((d) => {
        const v = (d.data() as AvionDoc).homeBaseIcao;
        if (v) set.add(v);
      });
      setBaseIcaos(set);
    });
    return () => {
      u1();
      u2();
      u3();
    };
  }, []);

  const aeroLookup = useMemo(
    () => new Map(aeros.map((a) => [a.id, a.displayCombined])),
    [aeros]
  );

  if (rows === null) {
    return <p className="text-sm text-slate-500">Cargando…</p>;
  }

  const term = filter.trim().toUpperCase();
  const filtered = term
    ? rows.filter((r) => r.lo.includes(term) || r.hi.includes(term))
    : rows;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mb-2 h-[3px] w-8 rounded-full bg-brand-700" />
          <h1 className="text-2xl font-semibold tracking-tight text-brand-900">Distancias</h1>
          <p className="mt-1 text-sm text-slate-600">
            Memoria de distancias entre aeropuertos.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900"
        >
          + Nueva distancia
        </button>
      </header>

      <input
        type="text"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Buscar por ICAO (SCEL, SCLC, …)"
        className="block w-full max-w-md rounded-md border border-slate-300 px-3 py-1.5 text-sm uppercase focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
      />

      {/* Mobile cards. Desktop table on md+. */}
      <ul className="space-y-2 md:hidden">
        {filtered.length === 0 ? (
          <li className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-center text-sm text-slate-500">
            Sin distancias en este filtro.
          </li>
        ) : (
          filtered.map((r) => {
            const flip = baseIcaos.has(r.hi) && !baseIcaos.has(r.lo);
            const left = flip ? r.hi : r.lo;
            const right = flip ? r.lo : r.hi;
            return (
              <li
                key={r.id}
                onClick={() => setEditing(r)}
                className="cursor-pointer rounded-lg border border-slate-200 bg-white shadow-sm p-3 text-sm"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-mono text-xs text-slate-700">
                    {left.replace(/^_/, "")} ↔ {right.replace(/^_/, "")}
                  </div>
                  <div className="font-mono text-sm text-slate-900">
                    {Math.round(r.distanceNm)} NM
                  </div>
                </div>
                <div className="mt-1 text-xs text-slate-700">
                  {aeroLookup.get(left) ?? left}{" "}
                  <span className="text-slate-500">
                    ↔ {aeroLookup.get(right) ?? right}
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-end text-xs">
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (
                        confirmDestructive(
                          `¿Borrar distancia ${r.lo}↔${r.hi}?`
                        )
                      ) {
                        await deleteDoc(doc(db, "Distancias", r.id));
                      }
                    }}
                    className="text-rose-700 hover:text-rose-900"
                  >
                    Borrar
                  </button>
                </div>
              </li>
            );
          })
        )}
      </ul>

      <div className="hidden overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm md:block">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Origen</th>
              <th className="px-3 py-2">Destino</th>
              <th className="px-3 py-2 text-right">NM</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((r) => {
              // If either endpoint is a base, force it to render in the
              // left "Origen" column. Otherwise keep the canonical
              // LO|HI sort order so reads remain stable.
              const flip =
                baseIcaos.has(r.hi) && !baseIcaos.has(r.lo);
              const left = flip ? r.hi : r.lo;
              const right = flip ? r.lo : r.hi;
              return (
              <tr
                key={r.id}
                onClick={() => setEditing(r)}
                className="cursor-pointer hover:bg-sky-50"
              >
                <td className="px-3 py-2">
                  <div className="text-slate-900">
                    {aeroLookup.get(left) ?? left}
                  </div>
                  <div className="font-mono text-xs text-slate-500">
                    {left.replace(/^_/, "")}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="text-slate-900">
                    {aeroLookup.get(right) ?? right}
                  </div>
                  <div className="font-mono text-xs text-slate-500">
                    {right.replace(/^_/, "")}
                  </div>
                </td>
                <td className="px-3 py-2 text-right font-mono text-slate-900">
                  {Math.round(r.distanceNm)}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (confirmDestructive(`¿Borrar distancia ${r.lo}↔${r.hi}?`)) {
                        await deleteDoc(doc(db, "Distancias", r.id));
                      }
                    }}
                    className="text-xs text-rose-700 hover:text-rose-900"
                  >
                    Borrar
                  </button>
                </td>
              </tr>
              );
            })}
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-8 text-center text-sm text-slate-500"
                >
                  Sin distancias en este filtro.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {creating ? (
        <DistanciaEditor
          aeros={aeros}
          initial={null}
          onClose={() => setCreating(false)}
        />
      ) : null}
      {editing ? (
        <DistanciaEditor
          aeros={aeros}
          initial={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

function DistanciaEditor({
  aeros,
  initial,
  onClose,
}: {
  aeros: AeropuertoRow[];
  initial: DistanciaRow | null;
  onClose: () => void;
}) {
  const isEdit = initial !== null;
  const [a, setA] = useState(initial?.lo ?? "");
  const [b, setB] = useState(initial?.hi ?? "");
  const [nm, setNm] = useState(initial ? String(initial.distanceNm) : "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    setError(null);
    const dn = Number(nm);
    if (!a) return setError("Selecciona origen.");
    if (!b) return setError("Selecciona destino.");
    if (a === b) return setError("Origen y destino no pueden ser iguales.");
    if (!Number.isFinite(dn) || dn <= 0)
      return setError("Distancia debe ser un número positivo.");
    setSaving(true);
    try {
      const id = distanciaId(a, b);
      const [lo, hi] = a < b ? [a, b] : [b, a];
      // On edit, if the chosen pair changed, the old doc id is now
      // stale — drop it before writing the new one so the table stays
      // canonical.
      if (isEdit && id !== initial!.id) {
        await deleteDoc(doc(db, "Distancias", initial!.id));
      }
      await setDoc(doc(db, "Distancias", id), {
        lo,
        hi,
        distanceNm: dn,
        source: "manual",
        lastVerifiedAt: serverTimestamp(),
        createdAt: isEdit ? initial!.createdAt : serverTimestamp(),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <SavingOverlay open={saving} label="Guardando…" />
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-slate-900">
          {isEdit ? "Editar distancia" : "Nueva distancia"}
        </h2>
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Origen
            </label>
            <select
              value={a}
              onChange={(e) => setA(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            >
              <option value="" disabled>
                Selecciona…
              </option>
              {aeros.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.displayCombined}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Destino
            </label>
            <select
              value={b}
              onChange={(e) => setB(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            >
              <option value="" disabled>
                Selecciona…
              </option>
              {aeros.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.displayCombined}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Distancia (NM)
            </label>
            <input
              type="number"
              value={nm}
              onChange={(e) => setNm(e.target.value)}
              min={1}
              step={0.1}
              className="mt-1 block w-32 rounded-md border border-slate-300 px-3 py-2 font-mono text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            />
          </div>
          {error ? (
            <p className="rounded border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
              {error}
            </p>
          ) : null}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={save}
            className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900 disabled:opacity-50"
          >
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

